import request, { IWorkspaceResponse } from '/@/api/http/request'
import { getDrcControlHeaders, getDrcControlSession, isDrcControlSessionActive } from '/@/utils/drc-lease'
import {
  PlaySpeakerAudioParams,
  SpeakerActionBody,
  SpeakerOperation,
  SpeakerSettingsBody,
  SpeakerStatusResponse,
  SpeakerTelemetry,
} from '/@/types/speaker'

export type {
  PlaySpeakerAudioParams,
  SpeakerActionBody,
  SpeakerAudioSource,
  SpeakerOperation,
  SpeakerSettingsBody,
  SpeakerStatusResponse,
  SpeakerTelemetry,
} from '/@/types/speaker'

const API_PREFIX = '/control/api/v1'

function requestHeaders (dockSn: string, useDrcHeaders: boolean) {
  if (!useDrcHeaders) return {}
  const session = getDrcControlSession(dockSn)
  if (!isDrcControlSessionActive(session, dockSn)) {
    // Never let a cockpit command silently fall back to the ground-debug
    // authorization path when its owner/fence lease is missing or expired.
    throw new Error('DRC 控制租约已失效，请重新获取控制权')
  }
  return getDrcControlHeaders(dockSn)
}

export async function getSpeakerStatus (
  dockSn: string,
  useDrcHeaders = false
): Promise<IWorkspaceResponse<SpeakerStatusResponse>> {
  const response = await request.get(`${API_PREFIX}/devices/${dockSn}/speaker/status`, {
    headers: requestHeaders(dockSn, useDrcHeaders),
    skipErrorMessage: true,
  } as any)
  return response.data
}

export interface PlaySpeakerAudioOptions {
  /** Live segments retry a busy speaker themselves; keep the global toast quiet. */
  skipErrorMessage?: boolean
}

export async function playSpeakerAudio (
  dockSn: string,
  params: PlaySpeakerAudioParams,
  useDrcHeaders = false,
  options: PlaySpeakerAudioOptions = {}
): Promise<IWorkspaceResponse<SpeakerOperation>> {
  const form = new FormData()
  form.append('psdk_index', String(params.psdkIndex))
  form.append('file_name', params.fileName)
  form.append('source', params.source)
  form.append('duration_ms', String(params.durationMs))
  form.append('file', params.file, params.fileName)
  const response = await request.post(`${API_PREFIX}/devices/${dockSn}/speaker/audio/play`, form, {
    headers: requestHeaders(dockSn, useDrcHeaders),
    skipErrorMessage: options.skipErrorMessage === true,
  } as any)
  return response.data
}

export async function updateSpeakerSettings (
  dockSn: string,
  body: SpeakerSettingsBody,
  useDrcHeaders = false
): Promise<IWorkspaceResponse<SpeakerTelemetry>> {
  const response = await request.put(`${API_PREFIX}/devices/${dockSn}/speaker/settings`, body, {
    headers: requestHeaders(dockSn, useDrcHeaders),
  })
  return response.data
}

export async function stopSpeakerAudio (
  dockSn: string,
  body: SpeakerActionBody,
  useDrcHeaders = false
): Promise<IWorkspaceResponse<SpeakerOperation>> {
  const response = await request.post(`${API_PREFIX}/devices/${dockSn}/speaker/stop`, body, {
    headers: requestHeaders(dockSn, useDrcHeaders),
  })
  return response.data
}

export async function replaySpeakerAudio (
  dockSn: string,
  body: SpeakerActionBody,
  useDrcHeaders = false
): Promise<IWorkspaceResponse<SpeakerOperation>> {
  const response = await request.post(`${API_PREFIX}/devices/${dockSn}/speaker/replay`, body, {
    headers: requestHeaders(dockSn, useDrcHeaders),
  })
  return response.data
}
